import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../utils/axios';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { toast } from 'sonner';
import { Download, Loader2, PlayCircle, SearchCheck, XCircle, AlertTriangle } from 'lucide-react';
import { BusquedaResultadosTabla } from './BusquedaResultadosTabla';
import { ESTADOS, esActiva, fmtFechaHora } from './formato';

const Stat = ({ label, value, testid, tono = 'text-slate-900' }) => (
  <div className="px-4 py-2 rounded-md bg-white border border-slate-200">
    <div className={`text-xl font-semibold tabular-nums ${tono}`} data-testid={testid}>{value}</div>
    <div className="text-[11px] text-slate-500">{label}</div>
  </div>
);

const ListaErrores = ({ errores }) => (
  <details className="text-xs text-slate-600" data-testid="busqueda-errores">
    <summary className="cursor-pointer text-orange-700"><AlertTriangle size={12} className="inline mr-1" />{errores.length} billetera(s) no se pudieron revisar (podés reintentarlas con "Reanudar")</summary>
    <ul className="mt-1 ml-4 list-disc">
      {errores.map(e => <li key={e.id_billetera}>{e.nombre || e.id_billetera} — {e.error}</li>)}
    </ul>
  </details>
);

export const BusquedaMovimientosPanel = ({ busquedaNueva }) => {
  const [historial, setHistorial] = useState([]);
  const [busquedaId, setBusquedaId] = useState(null);
  const [data, setData] = useState(null);
  const [accion, setAccion] = useState(null);
  const [ver, setVer] = useState(0);
  const elegir = useCallback((id) => { setData(null); setBusquedaId(id); }, []);

  const cargarHistorial = useCallback(async (seleccionar) => {
    try {
      const r = await api.get('/reportes/saldos/busqueda');
      const lista = r.data?.busquedas || [];
      setHistorial(lista);
      if (seleccionar && lista.length) setBusquedaId(id => id || lista[0].id);
    } catch { /* ignore */ }
  }, []);

  const cargar = useCallback(async (id) => {
    try {
      const r = await api.get(`/reportes/saldos/busqueda/${id}`);
      setData(r.data);
      return r.data.busqueda.estado;
    } catch { return null; }
  }, []);

  useEffect(() => { cargarHistorial(true); }, [cargarHistorial]);
  useEffect(() => { if (busquedaNueva) { elegir(busquedaNueva); cargarHistorial(false); } }, [busquedaNueva, cargarHistorial, elegir]);

  useEffect(() => {
    if (!busquedaId) return undefined;
    let timer;
    let vivo = true;
    const tick = async () => {
      const estado = await cargar(busquedaId);
      if (!vivo) return;
      if (esActiva(estado)) timer = setTimeout(tick, 4000);
      else cargarHistorial(false);
    };
    tick();
    return () => { vivo = false; clearTimeout(timer); };
  }, [busquedaId, ver, cargar, cargarHistorial]);

  const ejecutar = async (tipo) => {
    setAccion(tipo);
    try {
      await api.post(`/reportes/saldos/busqueda/${busquedaId}/${tipo}`);
      toast.success(tipo === 'cancelar' ? 'Se cancela al terminar la tanda en curso' : 'Búsqueda reanudada');
      setVer(v => v + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo completar la acción');
    } finally { setAccion(null); }
  };

  const exportar = async () => {
    setAccion('csv');
    try {
      const r = await api.get(`/reportes/saldos/busqueda/${busquedaId}/csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `busqueda_movimientos_${busquedaId.slice(0, 8)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('No se pudo exportar'); } finally { setAccion(null); }
  };

  if (!busquedaId) return null;
  const b = data?.busqueda;
  const resultados = data?.resultados || [];
  const errores = data?.errores || [];
  const activa = b && esActiva(b.estado);
  const pct = b?.total ? Math.min(100, (b.procesadas / b.total) * 100) : 0;
  const est = ESTADOS[b?.estado] || ESTADOS.PENDIENTE;
  const puedeReanudar = b && !activa && (b.estado !== 'COMPLETADA' || errores.length > 0);

  return (
    <Card className="p-4 border-blue-200 bg-blue-50/30 space-y-3" data-testid="busqueda-movs-panel">
      <div className="flex flex-wrap items-center gap-2">
        <SearchCheck size={18} className="text-blue-600" />
        <span className="font-semibold text-slate-800">Búsqueda en movimientos</span>
        {b && <Badge className={`text-[10px] ${est.cls}`} data-testid="busqueda-estado">{activa && <Loader2 size={10} className="mr-1 animate-spin" />}{est.label}</Badge>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Select value={busquedaId} onValueChange={elegir}>
            <SelectTrigger className="w-72 h-8 bg-white text-xs" data-testid="busqueda-historial-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {historial.map(h => (
                <SelectItem key={h.id} value={h.id} className="text-xs">"{h.criterio_texto}" · {fmtFechaHora(h.created_at)} · {(ESTADOS[h.estado] || {}).label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {activa && (
            <Button size="sm" variant="outline" onClick={() => ejecutar('cancelar')} disabled={!!accion || b.cancelar} className="h-8" data-testid="busqueda-cancelar-btn">
              <XCircle size={14} className="mr-1" /> {b.cancelar ? 'Cancelando...' : 'Cancelar'}
            </Button>
          )}
          {puedeReanudar && (
            <Button size="sm" variant="outline" onClick={() => ejecutar('reanudar')} disabled={!!accion} className="h-8" data-testid="busqueda-reanudar-btn">
              <PlayCircle size={14} className="mr-1" /> Reanudar
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={exportar} disabled={!resultados.length || !!accion} className="h-8" data-testid="busqueda-exportar-btn">
            {accion === 'csv' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />} Exportar CSV
          </Button>
        </div>
      </div>

      {!b ? (
        <div className="text-sm text-slate-400 py-4 text-center"><Loader2 size={16} className="inline animate-spin mr-1" /> Cargando búsqueda...</div>
      ) : (
        <>
          <div className="text-sm text-slate-600" data-testid="busqueda-criterio">
            Buscando <b className="text-slate-900">"{b.criterio_texto}"</b> del {b.desde} al {b.hasta}
            {b.tanda_actual > 0
              ? <> · Tanda {b.tanda_actual} de {b.total_tandas}</>
              : activa ? <> · Conectando con Waya...</> : <> · {b.total_tandas} tanda(s)</>}
            {b.error && <span className="text-red-600"> · {b.error}</span>}
          </div>
          <div className="h-2 bg-white rounded-full overflow-hidden border border-slate-200">
            <div className={`h-full transition-[width] duration-500 ${b.estado === 'ERROR' ? 'bg-red-500' : activa ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${pct}%` }} data-testid="busqueda-progreso" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Stat label="billeteras revisadas" value={`${b.procesadas} / ${b.total}`} testid="busqueda-stat-revisadas" />
            <Stat label="billeteras donde aparece" value={b.con_coincidencias} testid="busqueda-stat-billeteras" tono="text-blue-700" />
            <Stat label="veces en total" value={b.coincidencias_total} testid="busqueda-stat-veces" tono="text-blue-700" />
            {b.errores > 0 && <Stat label="con error" value={b.errores} testid="busqueda-stat-errores" tono="text-orange-600" />}
          </div>
          {errores.length > 0 && <ListaErrores errores={errores} />}
          {resultados.length ? (
            <BusquedaResultadosTabla busquedaId={busquedaId} resultados={resultados} />
          ) : (
            <div className="text-sm text-slate-500 py-6 text-center bg-white border border-dashed border-slate-200 rounded-md" data-testid="busqueda-sin-resultados">
              {activa ? 'Todavía no apareció en ninguna billetera revisada...' : `No apareció en ninguna de las ${b.procesadas} billeteras revisadas.`}
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default BusquedaMovimientosPanel;
